import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Mic, Radio, Music, DollarSign, Calendar, TrendingUp, PenSquare, Users, Sparkles, X, Trophy
} from 'lucide-react';

const ICON_MAP = {
  mic: Mic,
  radio: Radio,
  music: Music,
  'dollar-sign': DollarSign,
  calendar: Calendar,
  'trending-up': TrendingUp,
  'pen-square': PenSquare,
  users: Users,
  sparkles: Sparkles,
  trophy: Trophy,
};

function PromoCard({ promo, onDismiss, onPostCreated }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const Icon = ICON_MAP[promo.icon] || Sparkles;

  const handleClick = async () => {
    // Track click
    try {
      await fetch(`${API}/promotions/click/${promo.promo_id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch { /* ignore */ }

    // If it's a voice battle promo, trigger voice recording
    if (promo.promo_id === 'promo_voice_battle' && onPostCreated) {
      // Dispatch event to open voice recorder
      window.dispatchEvent(new CustomEvent('openVoiceRecorder'));
      return;
    }
    navigate(promo.cta_link);
  };

  const handleDismiss = async (e) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/promotions/dismiss/${promo.promo_id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch { /* ignore */ }
    onDismiss?.(promo.promo_id);
  };

  return (
    <Card
      className={`relative overflow-hidden border ${promo.border_color || 'border-border/50'} bg-gradient-to-r ${promo.gradient || 'from-violet-500/10 to-purple-500/10'} 
        hover:shadow-md transition-all duration-300 cursor-pointer group`}
      onClick={handleClick}
      data-testid={`promo-card-${promo.promo_id}`}
    >
      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-background/50 flex items-center justify-center
          opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background/80 z-10"
        data-testid={`dismiss-promo-${promo.promo_id}`}
      >
        <X className="w-3 h-3 text-muted-foreground" />
      </button>

      <div className="flex items-center gap-4 p-4">
        <div className={`w-11 h-11 rounded-xl bg-background/60 flex items-center justify-center shrink-0 ${promo.accent || 'text-primary'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm text-foreground line-clamp-1">{promo.title}</h4>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{promo.description}</p>
        </div>
        <Button
          size="sm"
          className={`shrink-0 text-xs font-bold h-8 px-4 rounded-full ${promo.accent?.replace('text-', 'bg-').replace('400', '600') || 'bg-primary'} text-white hover:opacity-90`}
          data-testid={`promo-cta-${promo.promo_id}`}
        >
          {promo.cta_text || 'Try Now'}
        </Button>
      </div>
    </Card>
  );
}

export function FeedPromoInsert({ position = 0, onPostCreated }) {
  const { isAuthenticated } = useAuth();
  const [promos, setPromos] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  const fetchPromos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/promotions/feed?limit=4`);
      if (res.ok) {
        const data = await res.json();
        setPromos(data.promos || []);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const handleDismiss = (promoId) => {
    setDismissed(prev => new Set([...prev, promoId]));
  };

  const visiblePromos = promos.filter(p => !dismissed.has(p.promo_id));

  // Show a promo based on position in feed
  const promo = visiblePromos[position % visiblePromos.length];
  if (!promo) return null;

  return (
    <div className="my-2 animate-in fade-in duration-500" data-testid="feed-promo-insert">
      <PromoCard promo={promo} onDismiss={handleDismiss} onPostCreated={onPostCreated} />
    </div>
  );
}

export function EmptyStatePromos() {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    fetch(`${API}/promotions/feed?limit=3`)
      .then(r => r.ok ? r.json() : { promos: [] })
      .then(d => setPromos(d.promos || []))
      .catch(() => {});
  }, []);

  if (!promos.length) return null;

  return (
    <div className="space-y-3 mt-4" data-testid="empty-state-promos">
      <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider px-1">Suggested for you</p>
      {promos.map(p => (
        <PromoCard key={p.promo_id} promo={p} onDismiss={() => {}} />
      ))}
    </div>
  );
}

export default PromoCard;
